import { Link } from 'react-router-dom';
import { newsList } from '@/mocks/news';
import { charactersList } from '@/mocks/characters';
import { eventsList } from '@/mocks/events';
import { guidesList } from '@/mocks/guides';
import { useSeo } from '@/hooks/useSeo';
import SectionHeading from '@/components/base/SectionHeading';
import NewsCard from '@/components/base/NewsCard';
import CharacterCard from '@/components/base/CharacterCard';
import EventCard from '@/components/base/EventCard';
import Reveal from '@/components/base/Reveal';
import HeroCarousel, { type HeroSlide } from './components/HeroCarousel';
import SidePanel from './components/SidePanel';

export default function Home() {
  useSeo({
    title: '原神游戏资讯与攻略大全 – 原神官方游戏站',
    description:
      '原神官网为玩家提供全面的原神游戏资讯、最新活动、角色攻略和版本更新信息，帮助玩家深入了解游戏世界，提升游戏体验。',
    keywords: '原神,原神资讯,原神攻略,原神角色,原神版本,原神活动,原神游戏,原神官方',
    canonicalPath: '/',
  });

  const featured = newsList.filter((item) => item.featured);
  const ongoingEvent = eventsList.find((item) => item.status === 'ongoing') ?? eventsList[0];

  const slides: HeroSlide[] = [
    ...featured.map((item) => ({
      id: item.id,
      title: item.title,
      excerpt: item.excerpt,
      cover: item.cover,
      to: `/news/${item.id}`,
      category: item.category,
    })),
    {
      id: ongoingEvent.id,
      title: `限时活动开启：${ongoingEvent.title}`,
      excerpt: ongoingEvent.intro,
      cover: ongoingEvent.cover,
      to: `/events/${ongoingEvent.id}`,
      category: '活动进行中',
    },
  ];

  const latestNews = newsList.slice(0, 5);
  const hotCharacters = [...charactersList].sort((a, b) => b.popularity - a.popularity).slice(0, 4);
  const promoEvents = eventsList.filter((item) => item.status !== 'ended').slice(0, 3);
  const hotGuides = guidesList.slice(0, 3);

  return (
    <div>
      <HeroCarousel slides={slides} />

      <section className="mx-auto w-full max-w-7xl px-4 py-14 md:px-6 md:py-20 lg:px-8">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <Reveal>
              <SectionHeading
                kicker="NEWS FLASH"
                title="资讯快报"
                subtitle="汇总最新游戏动态、官方公告与活动情报，掌握提瓦特第一手消息。"
                moreTo="/news"
                moreLabel="查看全部资讯"
              />
            </Reveal>
            <div className="space-y-4">
              {latestNews.map((item, index) => (
                <Reveal key={item.id} delay={index * 60}>
                  <NewsCard item={item} variant="wide" />
                </Reveal>
              ))}
            </div>
          </div>

          <div className="lg:col-span-1">
            <Reveal delay={120}>
              <SidePanel />
            </Reveal>
          </div>
        </div>
      </section>

      <section className="bg-background-50 py-14 md:py-20">
        <div className="mx-auto w-full max-w-7xl px-4 md:px-6 lg:px-8">
          <Reveal>
            <SectionHeading
              kicker="CHARACTERS"
              title="热门角色攻略导览"
              subtitle="人气角色培养思路、武器圣遗物搭配与实战配队，一站看完。"
              moreTo="/characters"
              moreLabel="全部角色"
            />
          </Reveal>
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {hotCharacters.map((item, index) => (
              <Reveal key={item.id} delay={index * 70}>
                <CharacterCard item={item} />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto w-full max-w-7xl px-4 py-14 md:px-6 md:py-20 lg:px-8">
        <Reveal>
          <SectionHeading
            kicker="EVENTS"
            title="活动推广"
            subtitle="当前进行中与即将开启的限时活动，奖励与参与建议一目了然。"
            moreTo="/events"
            moreLabel="全部活动"
          />
        </Reveal>
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
          {promoEvents.map((item, index) => (
            <Reveal key={item.id} delay={index * 70}>
              <EventCard item={item} />
            </Reveal>
          ))}
        </div>
      </section>

      <section className="bg-background-50 py-14 md:py-20">
        <div className="mx-auto w-full max-w-7xl px-4 md:px-6 lg:px-8">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-2 lg:items-center">
            <Reveal>
              <span className="inline-flex items-center gap-1.5 text-xs font-label font-medium tracking-widest text-accent-700">
                <i className="ri-shining-2-line"></i>
                ABOUT PLATFORM
              </span>
              <h2 className="mt-2 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
                一个为旅行者打造的原神资讯与攻略平台
              </h2>
              <p className="mt-4 text-sm leading-relaxed text-foreground-600 md:text-base">
                我们持续追踪版本动态、活动情报与角色数据，用结构化的内容与实用的攻略，帮助玩家更快理解游戏机制、更高效地规划养成路线。
              </p>
              <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-4">
                {[
                  { value: `${newsList.length}+`, label: '资讯条目' },
                  { value: `${charactersList.length}+`, label: '角色攻略' },
                  { value: `${guidesList.length}+`, label: '深度攻略' },
                  { value: `${eventsList.length}+`, label: '活动专题' },
                ].map((stat) => (
                  <div key={stat.label} className="rounded-lg border border-background-200 bg-background-50 p-3.5">
                    <p className="font-heading text-xl font-bold text-primary-700 md:text-2xl">{stat.value}</p>
                    <p className="mt-1 text-xs text-foreground-500">{stat.label}</p>
                  </div>
                ))}
              </div>
              <div className="mt-6 flex flex-wrap gap-3">
                <Link
                  to="/about"
                  className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-label font-medium text-background-50 transition-colors hover:bg-primary-600 dark:text-foreground-950"
                >
                  <i className="ri-information-line"></i>
                  了解我们
                </Link>
                <Link
                  to="/updates"
                  className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 px-5 py-2.5 text-sm font-label text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700"
                >
                  <i className="ri-history-line"></i>
                  版本更新日志
                </Link>
              </div>
            </Reveal>

            <Reveal delay={120}>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                {hotGuides.map((guide) => (
                  <Link
                    key={guide.id}
                    to={`/guides/${guide.id}`}
                    className="gs-card-hover group overflow-hidden rounded-xl border border-background-200 bg-background-50"
                  >
                    <div className="h-32 w-full overflow-hidden">
                      <img
                        src={guide.cover}
                        alt={guide.title}
                        title={`${guide.title} 原神攻略`}
                        className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                    </div>
                    <div className="p-3.5">
                      <span className="text-[11px] font-label text-accent-700">{guide.tags[0]}</span>
                      <h3 className="mt-1 line-clamp-2 text-sm font-medium text-foreground-900 transition-colors group-hover:text-primary-700">
                        {guide.title}
                      </h3>
                    </div>
                  </Link>
                ))}
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </div>
  );
}